import { useMemo } from 'react';

/**
 * Microsoft Clarity instrumentation.
 *
 * Clarity is loaded indirectly through GTM (see `utils/gtm.ts` /
 * `initializeGTM`), which only runs when GTM is enabled (production, or
 * `VITE_GTM_ENABLED=true`) AND the GTM container has a Clarity tag. That means
 * `window.clarity` is frequently undefined — in dev it never exists, and even
 * in prod it may not be ready until after GTM finishes loading. Every call here
 * is therefore null-safe: if `window.clarity` isn't a function yet, the call is
 * silently dropped rather than throwing.
 *
 * PHI / privacy (hard rule): NEVER pass PII into any Clarity call — no patient
 * IDs, order IDs, names, DOB, health-card numbers, or any free text from patient
 * data. Custom tags and events carry tab names / enum values only.
 *
 * Two ways to use this:
 *  - `useClarity()` hook — returns memoized callbacks (Patient360 tab tracking).
 *  - the standalone `trackSaveError` / `trackPatientTab` exports — for use deep
 *    inside large components/handlers where threading a hook through is awkward.
 *  Both call the same null-safe `clarity()` underneath.
 */

type ClarityFn = (...args: unknown[]) => void;

function clarity(...args: unknown[]): void {
  const c = (window as unknown as { clarity?: ClarityFn }).clarity;
  if (typeof c === 'function') c(...args);
}

// Filterable custom tag. NEVER pass PII (no patient/order IDs, names, DOB, card numbers).
export function setClarityTag(key: string, value: string): void {
  clarity('set', key, value);
}

// Custom event for counting interactions.
export function trackClarityEvent(name: string): void {
  clarity('event', name);
}

// Record the active Patient360 tab (tab enum slug only — never PII).
export function trackPatientTab(tab: string): void {
  setClarityTag('p360_tab', tab);
  trackClarityEvent(`p360_tab_${tab}`);
}

// Record a save failure, tagged by a non-PII surface slug (e.g.
// 'subscription_edit', 'subscription_create', 'billing_edit'). SUPPLEMENTARY
// signal only — it pairs with, never replaces, the user-facing inline error,
// because Clarity is frequently absent (dev, or before GTM loads in prod) and
// the event would be silently dropped. NEVER pass PII.
export function trackSaveError(surface: string): void {
  setClarityTag('save_surface', surface);
  trackClarityEvent('save_error');
}

export function useClarity() {
  return useMemo(
    () => ({
      setTag: setClarityTag,
      trackEvent: trackClarityEvent,
      trackPatientTab,
      trackSaveError,
    }),
    [],
  );
}
